import React from "react";
import { connect } from "react-redux";
import toast from "react-hot-toast";

import { Container, Row, Col, Form } from 'react-bootstrap';
import Sidebar from "../sidebar";
import Section from "../section";

import ConditionalButton from "../conditional-button";
import { update } from "../../actions/surveys";
import Modal, { initial } from "../modal";
import { deepEqual } from "../../utils/object";
import { richTextPlainText } from "../rich-text";

export const autosaveEnabled = (value) => value !== false;

export const sectionBrowserVisible = (sections, configured) => (
    !Array.isArray(sections)
    || sections.length !== 1
    || configured !== false
);

export const firstSectionIndex = (sections, fallback = 1) => {
    const first = Array.isArray(sections) ? sections[0] : null;
    return first?.index ?? fallback;
};

export const topLevelSections = (sections = []) => {
    if (!Array.isArray(sections)) return [];

    const collectionSectionIds = new Set(
        sections.flatMap(section => (section.Questions || [])
            .filter(question => question.typeName === "COLLECTION" && question.collectionOfUuid)
            .map(question => String(question.collectionOfUuid)))
    );

    return sections.filter(section => !collectionSectionIds.has(String(section.uuid || section.id)));
};

const missingAnswer = value => (
    value === null
    || value === undefined
    || (typeof value === "string" && !value.trim())
    || (Array.isArray(value) && !value.length)
);

export const requiredAnswerErrors = (sections = [], data = {}) => {
    const errors = [];
    sections.forEach(section => {
        const sectionData = data?.[section.key] || {};
        (section.Questions || []).forEach(question => {
            if (question.required && missingAnswer(sectionData?.[question.key])) {
                errors.push({
                    sectionKey: section.key,
                    questionKey: question.key,
                    message: `${richTextPlainText(question.label) || "This question"} is required.`,
                });
            }
        });
    });
    return errors;
};

const questionDomId = ({ sectionKey, questionKey }) =>
    `survey-question-${sectionKey}-${questionKey}`.replace(/[^A-Za-z0-9_-]/g, "-");

export class Template extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            autosave: autosaveEnabled(props.autosave),
            index: firstSectionIndex(props.Sections, props?.index ?? 1),
            data: props?.data ?? {},
            changed: false,
            modal: { ...initial },
            pendingSubmit: false,
            saveStatus: "idle",
            validationErrors: []
        };
        this.ref = React.createRef();
        this.autosaveInterval = null;
        this.changeVersion = 0;
        this.savePromise = null;
        this.saveToastId = `survey-progress-${props.invite || "response"}`;
    }
    
    componentDidMount() {
        // Start autosave interval
        this.autosaveInterval = setInterval(() => {
            if (this.state.autosave && this.state.changed) {
                this.onSave();
            }
        }, this.props.autosaveIntervalMs || 120000);
    }

    componentDidUpdate(prevProps) {
        const changes = {};

        if (prevProps.autosave !== this.props.autosave) {
            changes.autosave = autosaveEnabled(this.props.autosave);
        }

        if (prevProps.data !== this.props.data) {
            const data = this.props.data ?? {};
            if (!deepEqual(this.state.data, data)) {
                changes.data = data;
            }
        }

        if (prevProps.Sections !== this.props.Sections) {
            const previousSections = topLevelSections(prevProps.Sections);
            const sections = topLevelSections(this.props.Sections);
            const selectionExists = sections.some(
                section => section.index === this.state.index
            );
            if (
                sections.length
                && (!previousSections.length || !selectionExists)
            ) {
                changes.index = firstSectionIndex(sections, this.state.index);
            }
        }

        if (Object.keys(changes).length) {
            this.setState(changes);
        }
    }

    componentWillUnmount() {
        // Clean up interval to prevent memory leak
        if (this.autosaveInterval) {
            clearInterval(this.autosaveInterval);
        }
    }

    getCurrentData = () => {
        if (this.ref.current) {
            return this.ref.current.getData();
        }
        return this.state.data;
    }

    onChange = () => {
        this.changeVersion += 1;
        this.setState({ changed: true, validationErrors: [] });
    }

    doGetMergedData = () => {
        const currentData = this.getCurrentData() ?? {};
        return {
            ...(this.state.data ?? {}),
            ...currentData
        };
    }

    onClickSection = (evt, index, key, properties) => {
        this.setState({
            index, data: this.doGetMergedData()
        });
    }

    saveResponse = ({ submit = false, makePrivate = null } = {}) => {
        if (this.savePromise) return this.savePromise;

        this.savePromise = this.doSaveResponse({ submit, makePrivate })
            .finally(() => {
                this.savePromise = null;
            });

        return this.savePromise;
    }

    doSaveResponse = async ({ submit, makePrivate }) => {
        const data = this.doGetMergedData();
        if (submit) {
            const responseSections = topLevelSections(this.props.Sections);
            const validationErrors = requiredAnswerErrors(responseSections, data);
            if (validationErrors.length) {
                const first = validationErrors[0];
                const section = responseSections.find(
                    item => item.key === first.sectionKey
                );
                this.setState({
                    index: section?.index ?? this.state.index,
                    pendingSubmit: false,
                    saveStatus: "failed",
                    validationErrors,
                }, () => {
                    document.getElementById(questionDomId(first))?.focus();
                });
                toast.error(
                    `Answer ${validationErrors.length} required question${validationErrors.length === 1 ? "" : "s"} before submitting.`,
                    { id: this.saveToastId }
                );
                return false;
            }
        }
        const when = Date.now();
        const savedChangeVersion = this.changeVersion;
        const savingMessage = submit ? "Submitting response…" : "Saving progress…";
        const successMessage = submit ? "Response submitted." : "Progress saved.";
        const failureMessage = submit
            ? "We couldn't submit your response. Your answers are still here—please try again."
            : "We couldn't save your progress. Your answers are still here—please try again.";

        this.setState({ saveStatus: "saving" });
        toast.loading(savingMessage, { id: this.saveToastId });

        try {
            const meta = { save: { data, when } };
            if (submit) meta.submit = { data, when };

            const result = this.props.onPersist
                ? await this.props.onPersist({ data, submit })
                : await this.props.update(
                    this.props.invite,
                    meta,
                    submit ? (makePrivate || this.props.private) : null,
                    submit,
                    this.props.private
                );

            if (!result?.success) {
                throw new Error(result?.message || failureMessage);
            }

            const hasNewChanges = this.changeVersion !== savedChangeVersion;
            this.setState({
                data: hasNewChanges ? this.doGetMergedData() : data,
                changed: hasNewChanges,
                pendingSubmit: false,
                saveStatus: "saved"
            });
            toast.success(successMessage, { id: this.saveToastId });
            return true;
        } catch (error) {
            this.setState({
                changed: true,
                pendingSubmit: false,
                saveStatus: "failed"
            });
            toast.error(failureMessage, { id: this.saveToastId });
            return false;
        }
    }

    onSave = () => {
        return this.saveResponse();
    }

    onSubmit = () => {
        // Check if data is private, if not show modal
        if (!this.props.skipPrivacyPrompt && !this.props.private) {
            this.showPrivacyModal();
        } else {
            this.doSubmit(false);
        }
    }

    showPrivacyModal = () => {
        this.setState({
            pendingSubmit: true,
            modal: {
                show: true,
                title: "Confirm Privacy Setting",
                body: `This data can currently be seen by anyone with the link you clicked. Click "Hide & Submit" to require secure access to your answers. If you haven't set up a password, we'll email you a link to choose one. If you aren't logged in, you'll be redirected to the login page. This action is permanent.`,
                buttons: [
                    {
                        caption: "Hide & Submit",
                        onClick: this.onModalHide
                    },
                    {
                        caption: "Submit & Leave Visible",
                        onClick: this.onModalPublic
                    },
                    {
                        caption: "Cancel",
                        onClick: this.onModalCancel
                    }
                ]
            }
        });
    }

    onModalHide = async () => {
        // User wants to make data private
        this.closeModal();
        await this.doSubmit(true);
    }

    onModalPublic = async () => {
        // User wants to keep data public
        this.closeModal();
        await this.doSubmit(false);
    }

    onModalCancel = () => {
        // User cancelled submission
        this.closeModal();
        this.setState({ pendingSubmit: false });
    }

    closeModal = () => {
        this.setState({
            modal: { ...initial }
        });
    }

    doSubmit = async (makePrivate) => {
        this.setState({ pendingSubmit: true });
        return this.saveResponse({ submit: true, makePrivate });
    }

    onAutoSaveChange = (e) => {
        this.setState({
            autosave: e.target.checked
        });
    }
    
    render() {
        const responseSections = topLevelSections(this.props.Sections);
        const showSectionBrowser = sectionBrowserVisible(
            responseSections,
            this.props.showSectionBrowser ?? this.props.meta?.showSectionBrowser
        );

        return (
            <>
                <Container className="fera-survey-response-container">
                    <Row>
                        <Col md="7" lg="8" style={{ fontSize: "3rem" }} className="p-3">
                            {this.props.name}
                        </Col>
                        <Col md="5" lg="4" className="p-3">
                            <Row>
                                <Col md="6">
                                    <ConditionalButton
                                        className="mb-3 w-100"
                                        enabled={this.state.saveStatus !== "saving"}
                                        display={true}
                                        onEnabledClick={this.onSave}
                                        children={<>{this.state.saveStatus === "saving" ? "Saving…" : "Keep"}</>}
                                        tooltip="Keep my changes, but I'm not finished"
                                    />
                                </Col>
                                <Col md="6">
                                    <ConditionalButton
                                        className="mb-3 w-100"
                                        enabled={!this.state.pendingSubmit && this.state.saveStatus !== "saving"}
                                        display={true}
                                        onEnabledClick={this.onSubmit}
                                        children={<>Submit</>}
                                        tooltip="I'm all done"
                                    />
                                </Col>
                                <Col xs="12">
                                    <Form.Check 
                                        style={{ backgroundColor: "transparent", border: "none" }} 
                                        type="switch" 
                                        className="form-check-input" 
                                        id="autosave" 
                                        checked={this.state.autosave} 
                                        label={this.state.autosave ? "autosave" : "manual"} 
                                        onChange={this.onAutoSaveChange} 
                                    />
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                    <Row>
                        <Col className="p-3">{this.props.description}</Col>
                    </Row>
                    <Row className="h-100">
                        {showSectionBrowser ? (
                            <Col sm="2" className="p-3 text-center">
                                <Sidebar
                                    items={responseSections}
                                    selectedIndex={this.state.index}
                                    onClick={this.onClickSection}
                                />
                            </Col>
                        ) : null}
                        <Col sm={showSectionBrowser ? 10 : 12} className="p-3">
                            <Section 
                                ref={this.ref}
                                items={responseSections} 
                                selectedIndex={this.state.index} 
                                data={this.state.data} 
                                onChange={this.onChange} 
                                allowDynamicDisplay={this.props.allowDynamicDisplay}
                                collectionSections={this.props.Sections}
                                validationErrors={this.state.validationErrors}
                                richTextLabels={this.props.richTextLabels}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col sm="12" style={{ fontSize: "1rem" }} className="p-3 mt-5 text-center">
                            <img
                                className="fera-survey-footer-logo"
                                src={this.props?.logo}
                                alt="Logo"
                            />
                        </Col>
                        <Col sm="12" className="p-3 text-center" style={{ color: "silver" }}>
                            Powered by <a href="https://feradel.com" style={{ color: "inherit" }}>Feradel</a>
                        </Col>
                    </Row>
                </Container>
                <Modal 
                    show={this.state.modal?.show} 
                    title={this.state.modal?.title} 
                    body={this.state.modal?.body} 
                    buttons={this.state.modal?.buttons} 
                />
            </>
        );
    }
}

export default connect(null, { update })(Template);
